package com.example.readingnotes.presentation.auth

import com.example.readingnotes.domain.repository.AuthRepository
import com.example.readingnotes.domain.usecase.AdminLoginUseCase
import com.example.readingnotes.domain.usecase.RegisterUserUseCase
import com.example.readingnotes.domain.usecase.SetupTotpUseCase
import com.example.readingnotes.domain.usecase.VerifyTotpUseCase
import com.example.readingnotes.util.ErrorMessageProvider
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class AuthViewModel(
    private val adminLoginUseCase: AdminLoginUseCase,
    private val authRepository: AuthRepository,
    private val viewModelScope: CoroutineScope,
    private val registerUserUseCase: RegisterUserUseCase? = null,
    private val verifyTotpUseCase: VerifyTotpUseCase? = null,
    private val setupTotpUseCase: SetupTotpUseCase? = null,
) {
    private val _state = MutableStateFlow(AuthUiState())
    val state: StateFlow<AuthUiState> = _state.asStateFlow()

    fun onEvent(event: AuthEvent) {
        when (event) {
            is AuthEvent.OnPasswordChanged -> {
                _state.update { it.copy(passwordInput = event.text, errorMessage = null) }
            }
            is AuthEvent.OnLoginSubmit -> login()
            is AuthEvent.OnLogout -> logout()
            is AuthEvent.OnCheckAuthState -> checkAuthState()
            is AuthEvent.OnUsernameChanged -> {
                _state.update { it.copy(usernameInput = event.text, errorMessage = null) }
            }
            is AuthEvent.OnRegisterSubmit -> register()
            is AuthEvent.OnLoginWithUsernameSubmit -> loginWithUsername()
            is AuthEvent.OnTotpCodeChanged -> {
                _state.update { it.copy(totpCodeInput = event.code, errorMessage = null) }
            }
            is AuthEvent.OnVerifyTotpSubmit -> verifyTotp()
            is AuthEvent.OnSetupTotp -> setupTotp()
            // Email authentication handlers
            is AuthEvent.OnEmailChanged -> {
                _state.update { it.copy(emailInput = event.email, errorMessage = null) }
            }
            is AuthEvent.OnVerificationCodeChanged -> {
                _state.update { it.copy(verificationCodeInput = event.code, errorMessage = null) }
            }
            is AuthEvent.OnSendVerificationCode -> sendVerificationCode()
            is AuthEvent.OnVerifyEmailSubmit -> verifyEmail()
            is AuthEvent.OnLoginWithEmailSubmit -> loginWithEmail()
            // Progressive registration handlers
            is AuthEvent.OnCheckUsername -> checkUsernameAvailability(event.username)
            is AuthEvent.OnCompleteRegistration -> completeRegistration()
        }
    }

    private fun login() {
        val password = _state.value.passwordInput

        if (password.isBlank()) {
            _state.update { it.copy(errorMessage = "パスワードを入力してください") }
            return
        }

        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, errorMessage = null) }

            adminLoginUseCase(password)
                .onSuccess { authState ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            isLoggedIn = authState.isAdmin,
                            user = authState.user,
                            passwordInput = "",
                            errorMessage = null,
                            totpRequired = authState.user?.totpEnabled == true,
                        )
                    }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            isLoggedIn = false,
                            errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "ログインに失敗しました"),
                        )
                    }
                }
        }
    }

    private fun register() {
        val username = _state.value.usernameInput
        val password = _state.value.passwordInput

        if (registerUserUseCase == null) {
            _state.update { it.copy(errorMessage = "登録機能は利用できません") }
            return
        }

        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, errorMessage = null) }

            registerUserUseCase(username, password)
                .onSuccess { authState ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            isLoggedIn = authState.isAdmin,
                            user = authState.user,
                            usernameInput = "",
                            passwordInput = "",
                            errorMessage = null,
                        )
                    }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "登録に失敗しました"),
                        )
                    }
                }
        }
    }

    private fun loginWithUsername() {
        val username = _state.value.usernameInput
        val password = _state.value.passwordInput

        if (username.isBlank()) {
            _state.update { it.copy(errorMessage = "ユーザー名を入力してください") }
            return
        }
        if (password.isBlank()) {
            _state.update { it.copy(errorMessage = "パスワードを入力してください") }
            return
        }

        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, errorMessage = null) }

            authRepository
                .loginWithUsername(username, password)
                .onSuccess { authState ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            isLoggedIn = authState.isAdmin,
                            user = authState.user,
                            usernameInput = "",
                            passwordInput = "",
                            errorMessage = null,
                            totpRequired = authState.user?.totpEnabled == true,
                        )
                    }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "ログインに失敗しました"),
                        )
                    }
                }
        }
    }

    private fun verifyTotp() {
        val code = _state.value.totpCodeInput

        if (verifyTotpUseCase == null) {
            _state.update { it.copy(errorMessage = "TOTP検証機能は利用できません") }
            return
        }

        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, errorMessage = null) }

            verifyTotpUseCase(code)
                .onSuccess { verified ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            totpVerified = verified,
                            totpRequired = !verified,
                            totpCodeInput = "",
                            errorMessage = null,
                        )
                    }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "認証コードが無効です"),
                        )
                    }
                }
        }
    }

    private fun setupTotp() {
        if (setupTotpUseCase == null) {
            _state.update { it.copy(errorMessage = "TOTPセットアップ機能は利用できません") }
            return
        }

        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, errorMessage = null) }

            setupTotpUseCase()
                .onSuccess { response ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            totpSetupSecret = response.secret,
                            totpSetupQrCodeUrl = response.qrCodeUrl,
                            errorMessage = null,
                        )
                    }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "TOTPセットアップに失敗しました"),
                        )
                    }
                }
        }
    }

    private fun logout() {
        viewModelScope.launch {
            authRepository.logout()
            _state.update {
                AuthUiState(isLoggedIn = false)
            }
        }
    }

    private fun checkAuthState() {
        viewModelScope.launch {
            authRepository
                .getAuthState()
                .onSuccess { authState ->
                    _state.update {
                        it.copy(
                            isLoggedIn = authState.isAdmin,
                            user = authState.user,
                            totpRequired = authState.user?.totpEnabled == true && !authState.totpVerified,
                            totpVerified = authState.totpVerified,
                        )
                    }
                }.onFailure {
                    _state.update { it.copy(isLoggedIn = false) }
                }
        }
    }

    private fun sendVerificationCode() {
        val email = _state.value.emailInput

        if (email.isBlank()) {
            _state.update { it.copy(errorMessage = "メールアドレスを入力してください") }
            return
        }

        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, errorMessage = null) }

            authRepository
                .sendVerificationCode(email)
                .onSuccess {
                    _state.update {
                        it.copy(
                            isLoading = false,
                            verificationCodeSent = true,
                            errorMessage = null,
                        )
                    }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "認証コードの送信に失敗しました"),
                        )
                    }
                }
        }
    }

    private fun verifyEmail() {
        val email = _state.value.emailInput
        val code = _state.value.verificationCodeInput

        if (email.isBlank()) {
            _state.update { it.copy(errorMessage = "メールアドレスを入力してください") }
            return
        }
        if (code.isBlank()) {
            _state.update { it.copy(errorMessage = "認証コードを入力してください") }
            return
        }

        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, errorMessage = null) }

            authRepository
                .verifyEmail(email, code)
                .onSuccess { authState ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            isLoggedIn = authState.isAdmin,
                            user = authState.user,
                            emailVerified = true,
                            // Store temporary values for progressive registration
                            tempEmail = email,
                            tempVerificationCode = code,
                            emailInput = "",
                            verificationCodeInput = "",
                            verificationCodeSent = false,
                            errorMessage = null,
                        )
                    }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "認証コードが無効です"),
                        )
                    }
                }
        }
    }

    private fun loginWithEmail() {
        val email = _state.value.emailInput
        val code = _state.value.verificationCodeInput

        if (email.isBlank()) {
            _state.update { it.copy(errorMessage = "メールアドレスを入力してください") }
            return
        }

        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, errorMessage = null) }

            authRepository
                .loginWithEmail(email, code)
                .onSuccess { authState ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            isLoggedIn = authState.isAdmin,
                            user = authState.user,
                            emailInput = "",
                            verificationCodeInput = "",
                            verificationCodeSent = false,
                            errorMessage = null,
                            totpRequired = authState.user?.totpEnabled == true,
                        )
                    }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "ログインに失敗しました"),
                        )
                    }
                }
        }
    }

    private fun checkUsernameAvailability(username: String) {
        if (username.length < 3) {
            _state.update { it.copy(usernameAvailable = null, usernameChecking = false) }
            return
        }

        viewModelScope.launch {
            _state.update { it.copy(usernameChecking = true, errorMessage = null) }

            authRepository
                .checkUsernameAvailable(username)
                .onSuccess { available ->
                    _state.update {
                        it.copy(
                            usernameChecking = false,
                            usernameAvailable = available,
                            errorMessage = if (available) null else "このユーザー名は既に使用されています",
                        )
                    }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            usernameChecking = false,
                            usernameAvailable = null,
                            errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "ユーザー名の確認に失敗しました"),
                        )
                    }
                }
        }
    }

    private fun completeRegistration() {
        val username = _state.value.usernameInput
        val password = _state.value.passwordInput
        val email = _state.value.tempEmail
        val verificationCode = _state.value.tempVerificationCode

        if (registerUserUseCase == null) {
            _state.update { it.copy(errorMessage = "登録機能は利用できません") }
            return
        }

        if (username.isBlank()) {
            _state.update { it.copy(errorMessage = "ユーザー名を入力してください") }
            return
        }
        if (username.length < 3 || username.length > 20) {
            _state.update { it.copy(errorMessage = "ユーザー名は3〜20文字で入力してください") }
            return
        }
        if (!username.matches(Regex("^[a-zA-Z0-9_-]+$"))) {
            _state.update { it.copy(errorMessage = "ユーザー名は英数字、ハイフン、アンダースコアのみ使用できます") }
            return
        }
        if (password.isBlank()) {
            _state.update { it.copy(errorMessage = "パスワードを入力してください") }
            return
        }
        if (password.length < 8) {
            _state.update { it.copy(errorMessage = "パスワードは8文字以上で入力してください") }
            return
        }

        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, errorMessage = null) }

            // First verify email with the stored code
            authRepository
                .verifyEmail(email, verificationCode)
                .onSuccess { _ ->
                    // Then register with username and password
                    registerUserUseCase(username, password)
                        .onSuccess { authState ->
                            _state.update {
                                it.copy(
                                    isLoading = false,
                                    isLoggedIn = authState.isAdmin,
                                    user = authState.user,
                                    usernameInput = "",
                                    passwordInput = "",
                                    tempEmail = "",
                                    tempVerificationCode = "",
                                    errorMessage = null,
                                )
                            }
                        }.onFailure { error ->
                            _state.update {
                                it.copy(
                                    isLoading = false,
                                    errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "登録に失敗しました"),
                                )
                            }
                        }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "メール認証に失敗しました"),
                        )
                    }
                }
        }
    }
}
